import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";

import {
    fetchMiddlePanelDiariesData,
    resetMiddlePanel,
    fetchWorkkardTodoOwners,
} from '../../actions/action';
import LoaderComponent        from '../../../../components/Loader';
import ListingComponent       from '../../../../components/ListingComponent';
import DiarieCell             from './diarieCell';
import WorkkardMultiOptionFilter  from '../Filter/multiOptionFilter';
import WorkkardSingleOptionFilter from '../Filter/singleOptionFilter';
import { DiariesFilterConstants, MiddlePanelConstant, PAGE_SIZE } from "../../constants";
import Icons      from "../../../../icons";
import * as CommonUtil from '../../../../common/util';
import EmptyScreen from '../../../../Emptyscreen';
import { DEFAULT_PRIORITY } from '@/common/constants';
import { getDiariesListData } from '@/containers/Diaries/actions';
import { useLiveRefresh } from '../../../../utils/useLiveRefresh';

// Constants
const NOOP = () => false;
const DEFAULT_SORT   = "SortByDueDate";
const DEFAULT_STATUS = "OPEN";

// Helpers
const buildFilterOption = (filterOption, type) => {
    const base = [].concat(filterOption);
    return base.includes(type)
        ? base
        : [...base, "activity", "workkard"];
};

const buildBasePayload = ({ cId, type, diaries, todoOwners, sortBy, skip = "0", take = "10" }) => ({
    cId,
    type,
    actId:        0,
    skip,
    take,
    sortBy:       sortBy ?? DEFAULT_SORT,
    sort:         diaries.filter.sort || DEFAULT_STATUS,
    rows:         10,
    todoOwners,
    filterOption: JSON.stringify(buildFilterOption(diaries.filter.filterOption, type)),
});

// Helpers
const normalizePriority = (value) => value === DEFAULT_PRIORITY ? DEFAULT_PRIORITY : value;

const Diaries = ({ history }) => {
    const dispatch = useDispatch();
    const { listingData } = useSelector(state => state.diaries.listing);

    // Avoids re-dispatching identical or overlapping requests
    const lastFetchRef = useRef(null);

    const activityDetail    = useSelector(s => s.workkardDetail.activityDetail);
    const workkard          = useSelector(s => s.workkardDetail.workkardDetail);
    const isMiddlePanelLoading = useSelector(s => s.workkardDetail.middlePanel.isLoading);
    const isLoading         = useSelector(s => s.isLoading);
    const sessionContext    = useSelector(s => s.sessionContextOidc);

    const userId = sessionContext.userView?.id;

    // Local state
    const [showFilter,          setShowFilter]                  = useState(false);
    const [showSort,            setShowSort]                    = useState(false);
    const [ownerUser,           setOwnerUser]                   = useState({});
    const [allDiaryOwnerIds,    setAllDiaryOwnerIds]            = useState([]);
    // const [selectedDiaryOwners, setSelectedDiaryOwners]         = useState([]);
    const [showAllUsersData,    setShowAllUsersData]            = useState(false);
    const [selectedPriorityFilter, setSelectedPriorityFilter]   = useState(DEFAULT_PRIORITY);
    const [selectedStatusFilter, setSelectedStatusFilter]       = useState(DEFAULT_STATUS);
    const [selectedSortOption, setSelectedSortOption]           = useState('SortByDueDate');

    // Memoized options
    const priorityFilterOptions = useMemo(() => Object.values(DiariesFilterConstants.Priority), []);
    const statusFilterOptions   = useMemo(() => Object.values(DiariesFilterConstants.Status),   []);
    const sortOptions           = useMemo(() => Object.values(DiariesFilterConstants.Sort),     []);

    // Derived diaries data
    const diaries = listingData?.data;    

    const appliedSortLabel = useMemo(() =>
        sortOptions.find(item => item.value === selectedSortOption)?.name ?? "Due Date",
    [sortOptions, selectedSortOption]);

    const setUrlHash = useCallback((hash) => {
        CommonUtil.redirectURL(history, {
            pathname: window.location.pathname,
            search: window.location.search,
            hash,
        });
    }, [history]);

    // Handlers
    // const setInitialState = useCallback((panel, newOwnersAdded = false) => {
    //     const { diaries } = panel;
    //     const ownerIds    = [userId];
    //     const selectedIds = diaries.filter.todoOwners.toString().includes(userId) ? [userId] : [];

    //     const newState = {
    //         All: {
    //             name:    "All",
    //             value:   "all",
    //             checked: false,
    //             options: [{
    //                 name:    "Me",
    //                 value:   `${userId}`,
    //                 checked: diaries.filter.todoOwners !== ""
    //                     ? diaries.filter.todoOwners.toString().includes(userId)
    //                     : !!userId,
    //             }],
    //         },
    //     };

    //     diaries.todoOwners?.forEach((data) => {
    //         const obj = {
    //             name:    data.OwnerName,
    //             value:   data.OwnerId,
    //             checked: diaries.filter.todoOwners.toString().includes(data.OwnerId),
    //         };
    //         newState.All.options.push(obj);
    //         if (obj.checked) selectedIds.push(data.OwnerId);
    //         ownerIds.push(data.OwnerId);
    //     });

    //     setOwnerUser(newState);
    //     setAllDiaryOwnerIds(ownerIds);
    //     setSelectedDiaryOwners(selectedIds);
    //     setShowAllUsersData(newOwnersAdded);
    // }, [userId]);

    const onChangeType = useCallback((selectedValue, filterData) => {
        const hash = `cd`;
        setUrlHash(hash);

        const payload = {
            type:       "diaries",
            value:      selectedValue,
            todoOwners: diaries.todoOwners,
            filter:     filterData ?? { ...diaries.filter },
            total:      diaries.total,
            sortBy:     diaries.sortBy,
        };
        dispatch(resetMiddlePanel(payload));
    }, [dispatch, diaries, setUrlHash]);

    // Base payload shared across all list fetches
    const basePayload = useMemo(() => ({
        cId         : workkard.Id,
        page_size   : PAGE_SIZE,
        page_no     : 1,
        priority    : normalizePriority(selectedPriorityFilter),
        status      : selectedStatusFilter
    }), [selectedPriorityFilter, selectedStatusFilter]);

    const fetchList = useCallback((overrides = {}) => {
        const payload = { ...basePayload, ...overrides };
        const signature = JSON.stringify(payload);

        // Skip if identical to the last dispatched request
        if (lastFetchRef.current === signature) return;

        lastFetchRef.current = signature;
        dispatch(getDiariesListData(payload));
    }, [dispatch, basePayload]);

    // A diary event on this claim has to refetch even though the query has not
    // changed, which is precisely what fetchList's dedupe suppresses.
    useLiveRefresh(['diary'], () => {
        lastFetchRef.current = null;
        fetchList();
    }, { workkardId: workkard?.Id });

    // const onScrollHandler = useCallback(() => {
    //     const { skip, take, total, filter, type } = diaries;
    //     if (skip + take >= total) return;

    //     const nextSkip = skip === 0 && take > 10 ? take : parseInt(skip) + parseInt(take);
    //     const nextTake = skip === 0 && take > 10 ? 10  : parseInt(take);

    //     dispatch(fetchMiddlePanelDiariesData({
    //         cId:          workkard.Id,
    //         type,
    //         actId:        0,
    //         skip:         nextSkip,
    //         take:         nextTake,
    //         sortBy:       DEFAULT_SORT,
    //         sort:         filter.sort,
    //         rows:         10,
    //         todoOwners:   filter.todoOwners,
    //         filterOption: JSON.stringify([...filter.filterOption, "activity", "workkard"]),
    //     }));
    // }, [dispatch, diaries, workkard.Id]);

    const onApplyPriorityFilter = useCallback((value) => {
        setSelectedPriorityFilter(value);
        fetchList({ priority: normalizePriority(value) });
    }, [fetchList]);

    const onApplyStatusFilter = useCallback((value) => {
        setSelectedStatusFilter(value);
        fetchList({ status: value });
    }, [fetchList]);

    const onApplyUserFilter = useCallback((e) => {
        const filterOptions = (e.All?.options ?? [])
            .filter(d => d.checked)
            .map(d => d.value);

        const showAll = filterOptions.length === 0 && allDiaryOwnerIds.length > 0;

        setOwnerUser(e);
        setShowAllUsersData(showAll);

        onChangeType(diaries.type, {
            filterOption: diaries.filter.filterOption,
            sort:         diaries.filter.sort,
            todoOwners:   filterOptions.toString(),
        });
    }, [diaries, allDiaryOwnerIds.length, onChangeType]);

    const onApplySort = useCallback((value) => {
        setSelectedSortOption(value);
        dispatch(fetchMiddlePanelDiariesData(
            buildBasePayload({
                cId:       workkard.Id,
                type:      diaries.type,
                diaries,
                todoOwners: diaries.filter.todoOwners,
                sortBy:    value,
            })
        ));
    }, [dispatch, diaries, workkard.Id]);

    const renderDiaries = useCallback((data) => {
        if (data?.length > 0) {
            return (
                <ListingComponent
                    data={data}
                    cellTemplate={DiarieCell}
                    // onScrollHandler={onScrollHandler}
                    onItemClick={NOOP}
                    type="div"
                    selectedId=""
                    classname="scrollArea"
                />
            );
        }
        return !(isLoading || isMiddlePanelLoading) && <EmptyScreen name="Emptydiaries" />;
    }, [
        // onScrollHandler, 
        isLoading, 
        isMiddlePanelLoading
    ]);

    useEffect(() => {
        if (!activityDetail || !sessionContext.UserView) return;

        const type = MiddlePanelConstant.WorkkardType;
        setUrlHash(`cd`);

        const payload = buildBasePayload({
            cId:       workkard.Id,
            type,
            diaries,
            todoOwners: diaries.filter.todoOwners || userId.toString(),
        });

        dispatch(fetchWorkkardTodoOwners(payload));
    }, []); // intentional mount-only

    useEffect(() => {
        return () => {
            dispatch(resetMiddlePanel({
                type:   "diaries",
                value:  diaries.type,
                filter: { ...diaries.filter, todoOwners: "" },
                total:  diaries.total,
            }));
        };
    }, []); // intentional mount-only

    // Diary data sync
    // useEffect(() => {
    //     if (!sessionContext.UserView || !showAllUsersData) return;

    //     const payload = buildBasePayload({
    //         cId:       workkard.Id,
    //         type:      MiddlePanelConstant.WorkkardType,
    //         diaries,
    //         todoOwners: showAllUsersData
    //             ? (allDiaryOwnerIds.length > 0 ? allDiaryOwnerIds.toString() : diaries.filter.todoOwners)
    //             : diaries.filter.todoOwners,
    //         sortBy:    diaries.sortBy,
    //         skip:      0,
    //         take:      "10",
    //     });

    //     if (payload.todoOwners !== "") {
    //         dispatch(fetchMiddlePanelDiariesData(payload));
    //     }

    //     setShowAllUsersData(false);
    // }, [showAllUsersData]);

    const containerClass = diaries?.data?.length < 1 ? "noDataFound" : "";

    return (
        <>
            {(isLoading || isMiddlePanelLoading) && <LoaderComponent />}

            <div className="filterBlock rowFlex">
                <div className="filterLeft">
                    <div className="dropDown alignMiddle mr pd__none" data-test="todoOwnerFilter">
                        <button type="button" className="alignMiddle black btn-cc btn-cc__transparent" onClick={() => setShowFilter(p => !p)}>
                            <Icons name="filter" className="icon mr" /> Filters
                        </button>
                        {showFilter && (
                            <WorkkardMultiOptionFilter
                                filterOptions={ownerUser}
                                onCheckChange={onApplyUserFilter}
                                onClose={() => setShowFilter(false)}
                                showHeader
                                showParentOption={false}
                            />
                        )}
                    </div>
                    <div className="formGroup">
                        <WorkkardSingleOptionFilter
                            data-test="priorityFilter"
                            filterOptions={priorityFilterOptions}
                            onApplyFilter={onApplyPriorityFilter}
                            isSelectOption
                            isOpenRight
                            selectedValue={selectedPriorityFilter}
                            styleClass="wth-3"
                        />
                        <WorkkardSingleOptionFilter
                            data-test="statusFilter"
                            filterOptions={statusFilterOptions}
                            onApplyFilter={onApplyStatusFilter}
                            isSelectOption
                            isOpenRight
                            selectedValue={selectedStatusFilter}
                            styleClass="wth-3"
                        />
                    </div>
                </div>

                <div className="filterRight">
                    <div className="dropDown ml__1 pd__none" data-test={`sortByFilter ${diaries?.sortBy}`}>
                        <button
                            type="button"
                            className="alignMiddle"
                            onClick={() => setShowSort(p => !p)}
                            title={`${appliedSortLabel}`}
                        >
                            <Icons name="sort" className="icon mr" />
                        </button>
                        {showSort && (
                            <WorkkardSingleOptionFilter
                                filterOptions={sortOptions}
                                isOpenRight
                                onApplyFilter={onApplySort}
                                isSelectOption={false}
                                onClose={() => setShowSort(false)}
                                appliedSort={selectedSortOption}
                                styleClass="wth-3"
                            />
                        )}
                    </div>
                </div>
            </div>

            <div className={`diaryMainContainer detailMainContainer ${containerClass}`}>
                {diaries && renderDiaries(diaries)}
            </div>
        </>
    );
};

export default Diaries;